/**
 * Punchlist longtext format contract (shared by read and write).
 *
 * Each punchlist entry is stored as one GW longtext line carrying a 1-based
 * counter:
 * - WRITE: the app sends one line per entry as { TDID, TextLine, Counter } with
 *   Counter = 1..n (see ConfirmationInstallationPaperworkModel.buildPunchlistLongtext).
 *   An empty punchlist is sent as a single blank line (Counter 1) so the backend
 *   marks it complete. The app never writes the counter into the text itself.
 * - READ: GW renders that counter back as a " <n> : " prefix on the line
 *   (e.g. " 1 : replace cable") and may wrap long entries across several lines.
 *   {@link parsePunchlistLines} reverses this: it merges wrapped lines, strips
 *   the counter prefix, and drops blanks.
 *
 * Keeping both sides described here is the single source of truth for the format.
 */

/**
 * Matches the leading counter that prefixes a punchlist entry coming from GW,
 * e.g. the " 1 : " in " 1 : replace cable". Only the counter is matched; the
 * rest of the line (which may legitimately contain ':') is left untouched.
 */
const PUNCHLIST_COUNTER_PREFIX = /^\s*\d+\s*:\s?/;

/**
 * True when a longtext line begins a new punchlist entry, i.e. it starts with a
 * numbered counter such as " 1 : ...". Wrapped continuation lines have no
 * counter and start with whitespace instead.
 *
 * @param line
 */
function startsNewEntry(line: string): boolean {
    return PUNCHLIST_COUNTER_PREFIX.test(line);
}

/**
 * The backend stores each punchlist as SAP longtext, which wraps a single entry
 * across multiple lines once it exceeds the line width. Every logical entry
 * begins with a numbered counter (e.g. " 1 : replace cable"); wrapped
 * continuation lines carry no counter and start with whitespace.
 *
 * This merges each continuation line back onto the entry it belongs to, so one
 * entry stays one entry instead of being split into several. The counter prefix
 * is preserved on the merged line so existing consumers can still strip it via
 * {@link stripPunchlistCounter}.
 *
 * Note: continuation lines are re-joined with a single space, which assumes the
 * backend wraps on word boundaries (continuation lines are indented). If the
 * backend ever hard-wraps mid-word this would insert a spurious space.
 *
 * @param lines Raw longtext lines for a single punchlist, in backend order.
 * @returns One string per logical entry, counter prefix retained.
 */
function mergeWrappedPunchlistLines(lines: string[]): string[] {
    return lines.reduce<string[]>((entries, line) => {
        if (entries.length === 0 || startsNewEntry(line)) {
            entries.push(line);
        } else {
            entries[entries.length - 1] = `${entries[entries.length - 1].trimEnd()} ${line.trim()}`;
        }
        return entries;
    }, []);
}

/**
 * Removes the leading " <number> : " counter from a punchlist entry and trims
 * surrounding whitespace, keeping the remaining text verbatim (including any
 * ':' the technician typed).
 *
 * @param entry A single punchlist entry, optionally prefixed with its counter.
 * @returns The entry text without its counter prefix.
 */
function stripPunchlistCounter(entry: string): string {
    return entry.trim().replace(PUNCHLIST_COUNTER_PREFIX, '');
}

/**
 * Parses the raw punchlist longtext lines coming from GW into clean, whole
 * entries: wrapped lines are merged back together, the counter prefix is
 * stripped, and blank entries are dropped. This is the single place that turns
 * the backend line format into the {@link string[]} the app works with, so no
 * delimiter round-trip is needed downstream.
 *
 * @param lines Raw longtext lines for a single punchlist, in backend order.
 * @returns One clean entry per element, ready for display/editing.
 */
export function parsePunchlistLines(lines: string[]): string[] {
    return mergeWrappedPunchlistLines(lines)
        .map(stripPunchlistCounter)
        .filter((entry) => entry.length > 0);
}
